import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "white",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2, 2.5),
}));

const StyledTitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
});

const StyledImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: 330,
  objectFit: "cover",
  display: "block",
  [theme.breakpoints.down("sm")]: {
    height: 235,
  },
}));

const StyledTextPanel = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const StyledFactChip = styled(Chip)(({ theme }) => ({
  justifyContent: "flex-start",
  height: "auto",
  minHeight: 34,
  borderRadius: 18,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : theme.palette.secondary.light,
  "& .MuiChip-label": {
    whiteSpace: "normal",
    paddingTop: 7,
    paddingBottom: 7,
    lineHeight: 1.35,
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5, 2.25),
  backgroundColor: theme.palette.background.paper,
}));

interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  detailedDescription: string;
  image: string;
  facts: string[];
}

interface ServiceDialogProps {
  open: boolean;
  onClose: () => void;
  service: Service | null;
}

export const ServiceDialog: React.FC<ServiceDialogProps> = ({
  open,
  onClose,
  service,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!service) return null;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={`dialog-title-${service.title}`}
      aria-describedby={`dialog-description-${service.title}`}
    >
      <StyledDialogTitle id={`dialog-title-${service.title}`}>
        <StyledTitleContainer>
          {service.icon}
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {service.title}
          </Typography>
        </StyledTitleContainer>

        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="Sluiten"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent id={`dialog-description-${service.title}`}>
        <Grid container>
          <Grid item xs={12} md={5}>
            <StyledImage src={service.image} alt={service.title} />
          </Grid>

          <Grid item xs={12} md={7}>
            <StyledTextPanel>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.75,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                {service.detailedDescription}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
              >
                Goed om te weten
              </Typography>

              <Stack spacing={1}>
                {service.facts.map((fact) => (
                  <StyledFactChip
                    key={fact}
                    icon={<CheckRoundedIcon fontSize="small" />}
                    label={fact}
                  />
                ))}
              </Stack>
            </StyledTextPanel>
          </Grid>
        </Grid>
      </StyledDialogContent>

      <StyledDialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          aria-label="Sluiten"
          sx={{ px: 3 }}
        >
          Sluiten
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};
